import { InfoCircleOutlined } from "@ant-design/icons";
import { Flex, Tooltip } from "antd";
import { DefaultOptionType } from "antd/es/select";
import { t } from "i18next";
import { formatDateDDMMYY } from "../../utils/dateFormatter";
import {
  FilterItem,
  FilterItemType,
  FilterItemValue,
} from "../filters/filters.types";
import { KategorijaEvidentiraneMarkice } from "../obligee/usedStamps/usedStamps.types";
import {
  MfinEvidentiranaMarkica,
  MfinListaEvidentiranihAkciznihMarkicaPayload,
} from "./ministry.types";

export const MFIN_EVIDENTIRANE_MARKICE_INITIAL_FILTERS: MfinListaEvidentiranihAkciznihMarkicaPayload =
  {
    pib: null,
    kategorijaEvidentiranihMarkicaId: null,
    brojMarkice: null,
    brojZahteva: null,
    uPrometu: null,
  };

const U_PROMETU_ALL = "all";

const getUPrometuOptions = (): DefaultOptionType[] =>
  [
    { label: t("EVIDENCIJA_MARKICA.FILTER_U_PROMETU"), value: true },
    { label: t("EVIDENCIJA_MARKICA.FILTER_VAN_PROMETA"), value: false },
    { label: t("EVIDENCIJA_MARKICA.FILTER_SVE"), value: U_PROMETU_ALL },
  ] as DefaultOptionType[];

const formatUPrometuForApi = (value: FilterItemValue): FilterItemValue =>
  value === U_PROMETU_ALL ? null : value;

const formatUPrometuForClient = (value: FilterItemValue): FilterItemValue =>
  value === null || value === undefined ? U_PROMETU_ALL : value;

const KATEGORIJE_SA_FILTEROM_BROJ_MARKICE = [
  KategorijaEvidentiraneMarkice.NEISKORISCENE,
  KategorijaEvidentiraneMarkice.ISKORISCENE_PROIZVODNJA,
  KategorijaEvidentiraneMarkice.ISKORISCENE_UVOZ,
  KategorijaEvidentiraneMarkice.OSTECENE_VIDLJIVE,
];

export const getMfinEvidentiraneMarkiceFilterItems = ({
  kategorijeEvidentiranja,
  hasResponse = false,
  selectedKategorijaEvidentiranihMarkicaId,
}: {
  kategorijeEvidentiranja?: Array<DefaultOptionType>;
  hasResponse?: boolean;
  selectedKategorijaEvidentiranihMarkicaId?: KategorijaEvidentiraneMarkice | null;
}): Array<FilterItem<MfinListaEvidentiranihAkciznihMarkicaPayload>> => {
  const filterItems: Array<
    FilterItem<MfinListaEvidentiranihAkciznihMarkicaPayload>
  > = [
    {
      fieldName: "pib",
      type: FilterItemType.TEXT,
      required: true,
      props: {
        placeholder: t("COMMON.PIB"),
      },
    },
    {
      fieldName: "kategorijaEvidentiranihMarkicaId",
      type: FilterItemType.SELECT,
      props: {
        placeholder: t("PLACEHOLDERS.KATEGORIJE_EVIDENTIRANJA"),
        options: kategorijeEvidentiranja,
      },
    },
  ];

  if (
    selectedKategorijaEvidentiranihMarkicaId ===
    KategorijaEvidentiraneMarkice.ISKORISCENE_PROIZVODNJA
  ) {
    filterItems.push({
      fieldName: "uPrometu",
      type: FilterItemType.SELECT,
      formatForApi: formatUPrometuForApi,
      formatForClient: formatUPrometuForClient,
      props: {
        placeholder: t("PLACEHOLDERS.STATUS"),
        options: getUPrometuOptions(),
        allowClear: false,
      },
    });
  }

  if (
    selectedKategorijaEvidentiranihMarkicaId !== null &&
    selectedKategorijaEvidentiranihMarkicaId !== undefined &&
    KATEGORIJE_SA_FILTEROM_BROJ_MARKICE.includes(
      selectedKategorijaEvidentiranihMarkicaId
    )
  ) {
    filterItems.push({
      fieldName: "brojMarkice",
      type: FilterItemType.TEXT,
      props: {
        placeholder: t("PLACEHOLDERS.BROJ_MARKICE"),
      },
    });
  }

  filterItems.push({
    fieldName: "brojZahteva",
    type: FilterItemType.TEXT,
    props: {
      placeholder: t("EVIDENCIJA_MARKICA.BROJ_ZAM_ZAHTEVA"),
    },
  });

  return filterItems;
};

export const sanitizeMfinEvidentiraneMarkiceFilters = (
  filters: MfinListaEvidentiranihAkciznihMarkicaPayload
): MfinListaEvidentiranihAkciznihMarkicaPayload => ({
  pib: filters.pib,
  brojZahteva: filters.brojZahteva,
  kategorijaEvidentiranihMarkicaId: filters.kategorijaEvidentiranihMarkicaId,
  uPrometu:
    filters.kategorijaEvidentiranihMarkicaId ===
    KategorijaEvidentiraneMarkice.ISKORISCENE_PROIZVODNJA
      ? filters.uPrometu
      : null,
  brojMarkice:
    filters.kategorijaEvidentiranihMarkicaId !== null &&
    filters.kategorijaEvidentiranihMarkicaId !== undefined &&
    KATEGORIJE_SA_FILTEROM_BROJ_MARKICE.includes(
      filters.kategorijaEvidentiranihMarkicaId
    )
      ? filters.brojMarkice
      : null,
});

const formatExcelCellValue = (val: unknown) => {
  if (val === null || val === undefined) return "";
  return String(val);
};

const formatOpseziUPrometuExcel = (
  info: MfinEvidentiranaMarkica["opseziUPrometuInfo"]
) => {
  if (!info) return "";
  const opseziText = info.opsezi
    .reduce<string[]>((acc, opseg) => {
      const parts = [
        opseg.opseg,
        opseg.kolicina,
        opseg.ambalaza ? `(${opseg.ambalaza})` : null,
        opseg.datumStavljanjaUPromet,
      ].filter(Boolean);
      return [...acc, parts.join(" - ")];
    }, [])
    .join("; ");
  return `${info.kolicina}${opseziText ? `: ${opseziText}` : ""}`;
};

export const getMfinEvidentiraneMarkiceTableColumns = (
  kategorijaEvidentiranihMarkicaId: KategorijaEvidentiraneMarkice
) => {
  const colDefs = [
    {
      title: t("EVIDENCIJA_MARKICA.BROJ_ZAM_ZAHTEVA"),
      dataIndex: "brojZahteva",
      width: "150px",
      isVisible: true,
    },
    {
      title: t("EVIDENCIJA_MARKICA.OZNAKA_VRSTE_MARKICE"),
      dataIndex: "markicaKategorijeVrste",
      width: "100px",
      isVisible: true,
    },
    {
      title: t("AKCIZNE_MARKICE.PERIOD"),
      dataIndex: "period",
      width: "150px",
      isVisible: [
        KategorijaEvidentiraneMarkice.NEISKORISCENE,
        KategorijaEvidentiraneMarkice.OSTECENE_VIDLJIVE,
        KategorijaEvidentiraneMarkice.OSTECENE_NEVIDLJIVE,
      ].includes(kategorijaEvidentiranihMarkicaId),
    },
    {
      title: t("EVIDENCIJA_MARKICA.OPSEG_SERIJSKIH_BROJEVA"),
      dataIndex: "opsegEvidentiranihMarkica",
      width: "200px",
      isVisible: [
        KategorijaEvidentiraneMarkice.ISKORISCENE_PROIZVODNJA,
        KategorijaEvidentiraneMarkice.ISKORISCENE_UVOZ,
        KategorijaEvidentiraneMarkice.NEISKORISCENE,
        KategorijaEvidentiraneMarkice.OSTECENE_VIDLJIVE,
      ].includes(kategorijaEvidentiranihMarkicaId),
    },
    {
      title: t("EVIDENCIJA_MARKICA.KOLICINA"),
      dataIndex: "kolicina",
      width: "120px",
      isVisible: [
        KategorijaEvidentiraneMarkice.ISKORISCENE_PROIZVODNJA,
        KategorijaEvidentiraneMarkice.ISKORISCENE_UVOZ,
        KategorijaEvidentiraneMarkice.NEISKORISCENE,
        KategorijaEvidentiraneMarkice.OSTECENE_NEVIDLJIVE,
      ].includes(kategorijaEvidentiranihMarkicaId),
    },
    {
      title: `${t("EVIDENCIJA_MARKICA.ZAPREMINA_AMBALAZE")} (L) /
                  ${t("EVIDENCIJA_MARKICA.KOLICINA")} (kg) ${t(
        "EVIDENCIJA_MARKICA.PO_POJEDINACNOM_PAKOVAJNU"
      )}`,
      dataIndex: "ambalaza",
      width: "200px",
      isVisible: [KategorijaEvidentiraneMarkice.ISKORISCENE_UVOZ].includes(
        kategorijaEvidentiranihMarkicaId
      ),
    },
    {
      title: t("EVIDENCIJA_MARKICA.KOLICINA"),
      dataIndex: "kolicinaOstecenihVidljivaSerija",
      width: "120px",
      isVisible: [KategorijaEvidentiraneMarkice.OSTECENE_VIDLJIVE].includes(
        kategorijaEvidentiranihMarkicaId
      ),
    },
    {
      title: t("EVIDENCIJA_MARKICA.DATUM_EVIDENTIRANJA"),
      dataIndex: "datumEvidentiranja",
      isVisible: [
        KategorijaEvidentiraneMarkice.NEISKORISCENE,
        KategorijaEvidentiraneMarkice.OSTECENE_NEVIDLJIVE,
        KategorijaEvidentiraneMarkice.OSTECENE_VIDLJIVE,
      ].includes(kategorijaEvidentiranihMarkicaId),
      render: (datum: string) => formatDateDDMMYY(datum),
    },
    {
      title: t("EVIDENCIJA_MARKICA.DATUM_PROIZVODNJE_ODNOSNO_CARINJENJA"),
      dataIndex: "opsegDatumaProizvodnje",
      width: "120px",
      isVisible: [
        KategorijaEvidentiraneMarkice.ISKORISCENE_PROIZVODNJA,
        KategorijaEvidentiraneMarkice.ISKORISCENE_UVOZ,
      ].includes(kategorijaEvidentiranihMarkicaId),
    },
    {
      title: t("EVIDENCIJA_MARKICA.LOKACIJA"),
      dataIndex: "lokacija",
      width: "120px",
      isVisible: [
        KategorijaEvidentiraneMarkice.ISKORISCENE_PROIZVODNJA,
        KategorijaEvidentiraneMarkice.ISKORISCENE_UVOZ,
      ].includes(kategorijaEvidentiranihMarkicaId),
    },
    {
      title: t("EVIDENCIJA_MARKICA.MASINA"),
      dataIndex: "masina",
      width: "120px",
      isVisible: [
        KategorijaEvidentiraneMarkice.ISKORISCENE_PROIZVODNJA,
        KategorijaEvidentiraneMarkice.ISKORISCENE_UVOZ,
      ].includes(kategorijaEvidentiranihMarkicaId),
      render: (masina: string) => masina ?? "-",
    },
    {
      title: t("EVIDENCIJA_MARKICA.BREND"),
      dataIndex: "brend",
      width: "120px",
      isVisible: [
        KategorijaEvidentiraneMarkice.ISKORISCENE_PROIZVODNJA,
        KategorijaEvidentiraneMarkice.ISKORISCENE_UVOZ,
      ].includes(kategorijaEvidentiranihMarkicaId),
    },
    {
      title: (
        <Tooltip
          title={t("EVIDENCIJA_MARKICA.STAVLJENO_U_PROMET_PRODAJU_INFO")}
        >
          {t("EVIDENCIJA_MARKICA.STAVLJENO_U_PROMET_PRODAJU")}&nbsp;
          <InfoCircleOutlined />
        </Tooltip>
      ),
      width: 400,
      isVisible: [
        KategorijaEvidentiraneMarkice.ISKORISCENE_PROIZVODNJA,
      ].includes(kategorijaEvidentiranihMarkicaId),
      render: (record: MfinEvidentiranaMarkica) => (
        <Flex gap={16} align="center">
          <strong style={{ fontFamily: "unset" }}>
            {record.opseziUPrometuInfo?.kolicina}
          </strong>
          <Flex gap={10} vertical>
            {record.opseziUPrometuInfo?.opsezi?.map((opseg, i) => (
              <Flex key={i} gap={8} align="center" wrap="wrap">
                <Flex align="end" gap={4}>
                  {opseg?.opseg && (
                    <>
                      <span style={{ fontFamily: "unset", color: "#1890ff" }}>
                        {opseg?.opseg}
                      </span>
                      <span>&nbsp;-&nbsp;</span>
                    </>
                  )}
                  <span style={{ fontFamily: "unset" }}>{opseg?.kolicina}</span>
                  {opseg?.ambalaza ? (
                    <span
                      style={{ fontSize: 12 }}
                    >{`(${opseg?.ambalaza})`}</span>
                  ) : (
                    ""
                  )}
                </Flex>
                <span>-</span>
                <span>{opseg.datumStavljanjaUPromet}</span>
              </Flex>
            ))}
          </Flex>
        </Flex>
      ),
    },
  ];

  return colDefs.filter((col) => col.isVisible);
};

export const getMfinEvidentiraneMarkiceExcelColumns = (
  kategorijaEvidentiranihMarkicaId: number
) => {
  const tableColumns = getMfinEvidentiraneMarkiceTableColumns(
    kategorijaEvidentiranihMarkicaId
  );

  return tableColumns.reduce<
    Array<{
      title: string;
      dataIndex: string;
      excelWidth?: number;
      excelValueFormatter?: (
        val: unknown,
        record?: MfinEvidentiranaMarkica
      ) => string;
    }>
  >((acc, col) => {
    if (!col.dataIndex) {
      if (col.render) {
        return [
          ...acc,
          {
            title: t("EVIDENCIJA_MARKICA.STAVLJENO_U_PROMET_PRODAJU"),
            dataIndex: "opseziUPrometuInfo",
            excelWidth: 40,
            excelValueFormatter: (val) =>
              formatOpseziUPrometuExcel(
                val as MfinEvidentiranaMarkica["opseziUPrometuInfo"]
              ),
          },
        ];
      }
      return acc;
    }

    return [
      ...acc,
      {
        title: typeof col.title === "string" ? col.title : "",
        dataIndex: col.dataIndex,
        excelWidth: 20,
        excelValueFormatter:
          col.dataIndex === "datumEvidentiranja"
            ? (val) => formatDateDDMMYY(val as string) ?? ""
            : formatExcelCellValue,
      },
    ];
  }, []);
};
